import { RefinedSubTrade } from 'app/models/traded-facilities-params'

export const extractFilenameDataFromRefinedTrade = (
  refinedSubTrade?: RefinedSubTrade | null,
) => {
  return {
    dealName:
      refinedSubTrade?.dealName || refinedSubTrade?.masterTrade?.dealName || '',
    subtradeId: refinedSubTrade?.systemId || refinedSubTrade?.id || '',
    buyerLegalName:
      refinedSubTrade?.buyerEntity?.entityName ||
      refinedSubTrade?.masterTrade?.buyerEntity?.entityName ||
      refinedSubTrade?.masterTrade?.counterpartyName ||
      '',
  }
}

export const sanitizeForFilename = (text: string): string => {
  return text
    .replace(/[<>:"/\\|?*]/g, '') // Remove invalid filename characters
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/_+/g, '_') // Replace multiple underscores with single
    .trim()
    .replace(/^_+|_+$/g, '') // Remove leading/trailing underscores
}

export const formatDealNameForFilename = (dealName: string): string => {
  if (!dealName) return 'Unknown_Deal'

  // Remove everything from the first opening parenthesis to the end
  const withoutParentheses = dealName.replace(/\s*\([^)]*\).*$/, '').trim()

  return sanitizeForFilename(withoutParentheses || 'Unknown_Deal')
}

// Sub-trade status lives on the ledger's LockedSubTrade contract, and every
// value it can carry (DAML LockedMasterTradeStatus) is terminal — so a sub-trade
// still to settle has no LockedSubTrade, and therefore no status.
//
// The FM template tests that directly, because the backend can see whether the
// contract exists. Here we only have the status string the API sent, so the same
// rule has to be expressed as its equivalent: not one of the terminal values.
// Keep this list in step with LockedMasterTradeStatus — omitting a member makes
// a terminal sub-trade look like a draft, which is how Terminated was missed
// first time round.
const TERMINAL_SUB_TRADE_STATUSES = ['Settled', 'Canceled', 'Terminated']

// An unknown or missing status counts as a draft: a memo wrongly marked
// provisional is recoverable, one wrongly presented as final is the risk
// LOS-6658 exists to remove.
export const isDraftSubTradeStatus = (status?: string | null): boolean =>
  !TERMINAL_SUB_TRADE_STATUSES.includes(status ?? '')

// Document type to suffix mapping
const DOCUMENT_TYPE_SUFFIXES = {
  AssignmentAgreementDocument: 'AA',
  ConfirmationDocument: 'Confirm',
  FundingMemoDocument: 'FM',
  AffiliateLetterDocument: 'AL',
} as const

export const getDocumentFilename = (
  documentType: string,
  dealName: string,
  subtradeId: string,
  buyerLegalName: string,
  isDraft = false,
): string => {
  const suffix =
    DOCUMENT_TYPE_SUFFIXES[documentType as keyof typeof DOCUMENT_TYPE_SUFFIXES]
  if (!suffix) {
    return 'document.pdf'
  }

  const formattedDealName = formatDealNameForFilename(dealName)
  const cleanSubtradeId = subtradeId || 'Unknown_Subtrade'
  const cleanBuyerName = sanitizeForFilename(buyerLegalName || 'Unknown_Buyer')
  // LOS-6658: only the Funding Memo has a draft convention — a memo downloaded
  // before the trade settles is marked so it can't be mistaken for a final one.
  const draftMarker =
    isDraft && documentType === 'FundingMemoDocument' ? '_DRAFT' : ''
  return `${formattedDealName}_${suffix}${draftMarker}_${cleanSubtradeId}_${cleanBuyerName}.pdf`
}
